// hc-pdp-shipping-helper.js
// <hc-pdp-shipping-helper> — contextual free-shipping message above PDP ATC.
// Subscribes to cart:change, fetches /cart.js, computes eligible subtotal
// (excludes line items in non-default delivery profiles: freight and 25 lb+
// items), and renders one of four states:
//
//   1. This product is excluded (freight or 25 lb+) → "Ships separately due to size or weight..."
//   2. (cart_eligible_subtotal + this_price) >= threshold → "Adding this unlocks free shipping"
//   3. cart_eligible_subtotal >= threshold → "Cart qualifies for free shipping"
//   4. else → "Add this and you'll be $X.XX from free shipping"

(function () {
  'use strict';

  if (!window.customElements) return;
  if (customElements.get('hc-pdp-shipping-helper')) return;

  var locale = (window.Shopify && Shopify.locale) || 'en-US';
  var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  var formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  var noDecimalFormatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  function eligibleSubtotal(cart) {
    var ltlProducts = new Set(window.HC_LTL_PRODUCT_IDS || []);
    var ltlVariants = new Set(window.HC_LTL_VARIANT_IDS || []);
    var sum = 0;
    (cart.items || []).forEach(function (item) {
      if (!item.requires_shipping) return;
      if (ltlProducts.has(item.product_id)) return;
      if (ltlVariants.has(item.variant_id)) return;
      sum += item.final_line_price || 0;
    });
    var discount = (cart.cart_level_discount_applications || []).reduce(function (s, d) {
      return s + (d.total_allocated_amount || 0);
    }, 0);
    return Math.max(0, sum - discount);
  }

  class HcPdpShippingHelper extends HTMLElement {
    connectedCallback() {
      this._msg = this.querySelector('.hc-pdp-ship__msg');
      this._price = parseInt(this.getAttribute('data-product-price'), 10) || 0;
      this._isLtl = this.getAttribute('data-product-is-ltl') === 'true';
      this._threshold = parseInt(this.getAttribute('data-threshold'), 10) || 9900;
      this._lastEligibleCents = 0;

      var ltlList = (this.getAttribute('data-product-ltl-variants') || '').split(',');
      this._ltlVariantIds = new Set();
      for (var i = 0; i < ltlList.length; i++) {
        var n = parseInt(ltlList[i], 10);
        if (!isNaN(n)) this._ltlVariantIds.add(n);
      }

      this._onCartChange = this._onCartChange.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);
      this._onQuantityChange = this._onQuantityChange.bind(this);
      document.addEventListener('cart:change', this._onCartChange);

      // Scope variant:change to this product's container so a related-product
      // form on the same page can't hijack the main PDP helper.
      this._scope = this.closest('.product-info') || this.closest('product-info') || document;
      this._scope.addEventListener('variant:change', this._onVariantChange);

      this._quantity = 1;
      this._quantityInput = this._scope.querySelector('input[name="quantity"]');
      if (this._quantityInput) {
        var initialQty = parseInt(this._quantityInput.value, 10);
        if (!isNaN(initialQty) && initialQty > 0) this._quantity = initialQty;
        this._quantityInput.addEventListener('change', this._onQuantityChange);
        this._quantityInput.addEventListener('input', this._onQuantityChange);
      }

      if (this._isLtl) this.setAttribute('data-state', 'ltl');

      // Always fetch the cart so we have an accurate eligible subtotal ready in
      // case the user switches to an eligible variant. When _isLtl is true, the
      // server-rendered LTL message stays put (we don't re-render).
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          this._lastEligibleCents = eligibleSubtotal(cart);
          if (!this._isLtl) this._render(this._lastEligibleCents);
        }.bind(this))
        .catch(function () { /* swallow */ });
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCartChange);
      if (this._scope) {
        this._scope.removeEventListener('variant:change', this._onVariantChange);
      }
      if (this._quantityInput) {
        this._quantityInput.removeEventListener('change', this._onQuantityChange);
        this._quantityInput.removeEventListener('input', this._onQuantityChange);
      }
    }

    _onCartChange(e) {
      var cart = e && e.detail && e.detail.cart;
      if (!cart) return;
      this._lastEligibleCents = eligibleSubtotal(cart);
      if (this._isLtl) return; // LTL message doesn't depend on cart total
      this._render(this._lastEligibleCents);
    }

    _onVariantChange(e) {
      var variant = e && e.detail && e.detail.variant;
      if (!variant) return;
      if (typeof variant.price === 'number') this._price = variant.price;
      if (typeof variant.id === 'number' || typeof variant.id === 'string') {
        var vid = parseInt(variant.id, 10);
        this._isLtl = this._ltlVariantIds.has(vid);
      }
      this._render(this._lastEligibleCents);
    }

    _onQuantityChange() {
      if (!this._quantityInput) return;
      var q = parseInt(this._quantityInput.value, 10);
      this._quantity = (!isNaN(q) && q > 0) ? q : 1;
      if (this._isLtl) return;
      this._render(this._lastEligibleCents);
    }

    _renderLtl() {
      this._msg.innerHTML = this._icon('truck') +
        ' Ships separately due to size or weight. Not eligible for free shipping.';
      this.setAttribute('data-state', 'ltl');
    }

    _render(eligibleCents) {
      if (!this._msg) return;

      // LTL state: this variant is in a non-default delivery profile.
      if (this._isLtl) {
        this._renderLtl();
        return;
      }

      var threshold = this._threshold;
      var lineTotal = this._price * this._quantity;

      // State A: cart already at/above threshold (without this product needed)
      if (eligibleCents >= threshold) {
        this._msg.innerHTML = this._icon('check') +
          ' Your cart qualifies for <span class="hc-pdp-ship__accent">free shipping</span>.';
        this.setAttribute('data-state', 'qualified');
        return;
      }

      // State B: adding this product at current quantity would cross the threshold
      if (eligibleCents + lineTotal >= threshold) {
        this._msg.innerHTML = this._icon('check') +
          ' Adding this unlocks <span class="hc-pdp-ship__accent">free shipping</span>.';
        this.setAttribute('data-state', 'unlocks');
        return;
      }

      // State C: still below threshold even after adding this at current quantity
      var remaining = threshold - (eligibleCents + lineTotal);
      var remainingLabel = formatter.format(remaining / 100);
      this._msg.innerHTML = this._icon('truck') +
        ' Add this and you’ll be <span class="hc-pdp-ship__accent">' + remainingLabel +
        '</span> from free shipping (' + noDecimalFormatter.format(threshold / 100) + ').';
      this.setAttribute('data-state', 'progress');
    }

    _icon(name) {
      if (name === 'check') {
        return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
      }
      // truck
      return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>';
    }
  }

  customElements.define('hc-pdp-shipping-helper', HcPdpShippingHelper);
})();
